import { downloadBlobFromResponse, getCsrfValue } from "../../../api/ApiHelper";
import { DateFormatType, formatNullableDate } from "../../../utils/DateFormatter";
import * as WeightUtils from "../../../utils/WeightUtils";
import instance from "../../../utils/i18n";
import { type OrderLineItemData } from "../../common/modelsV2";
import { type QuoteListRow } from "../useQuoteListState";
import { type ReportType } from "../../../Cart/components/CartPage/Buttons/DownloadReportButton/api/downloadCartReport";
import { resolveApiUrl } from "../../../utils/url/urlResolver";
import { getOrderLineItemTotalPrice } from "~/OrdersQuotes/common/utils/OrderLineItemUtils";
import type { QuoteLineMO } from "~/OrdersQuotes/common/components/v2/models";
import { getEDD } from "~/OrdersQuotes/common/components/v2/QuoteTable/utils";
import { translateWarehouseName } from "~/utils/language/translationUtils";

export const downloadQuoteHistoryReport = (
  data: QuoteListRow[],
  fileType: ReportType,
  labels: Dict<string>,
): Promise<void> => {
  const url = resolveApiUrl(`/my-account/quotes/search/report/${fileType}`);
  return fetch(url, {
    body: JSON.stringify(transformData(data, labels)),
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
    method: "POST",
    headers: {
      "content-type": "application/json",
      CSRFToken: getCsrfValue(),
    },
  }).then(downloadBlobFromResponse);
};

interface QuoteReportDownloadDTO {
  labels: Dict<string>;
  lines: QuoteReportDownloadLineDTO[];
}

interface QuoteReportDownloadLineDTO {
  quoteNumber: string;
  customerReference: string;
  dateEntered: string | undefined;
  quoteStatus: string;
  orderAttainment: string;
  quoteType: string;
  sublines: QuoteReportDownloadSubLineDTO[];
  account?: {
    code: string;
    salesOrg: string;
    endCustomerCodes: string[]; // Just one! It's a list because BE can't design make a proper API
  };
}

interface QuoteReportDownloadSubLineDTO {
  lineItem: string;
  sublineItem: string;
  product: string;
  description: string;
  materialNumber: string;
  customerMaterialNumber: string;
  packagingCode: string;
  quantity: number;
  confirmedQuantity: number;
  shippedQuantity: number;
  shipment: string;
  estimatedDeliveryDate: string;
  requestedDeliveryDate: string;
  weight: number | undefined;
  weightUnitOfMeasurement: string;
  weightUnit: string;
  netPrice: number | undefined;
  netPriceCurrency: string;
  priceUnit: string;
  totalNetPrice: number | undefined;
  currency: string;
  lineItemStatus: string;
  comment: string;
  priceReference: string;
  marketDesignation: string;
}

const transformData = (data: QuoteListRow[], labels: Dict<string>): QuoteReportDownloadDTO => {
  const lines: QuoteReportDownloadLineDTO[] = !data ? [] : data.map(transformLine);
  return {
    labels: labels,
    lines: lines,
  };
};

const transformLine = (row: QuoteListRow): QuoteReportDownloadLineDTO => {
  const sublines: QuoteReportDownloadSubLineDTO[] = row.details?.quoteLines.map(transformQuoteLine).flat() ?? [];

  return {
    quoteNumber: row.data.quoteNumber,
    customerReference: row.data.poNumber,
    dateEntered: formatNullableDate(row.data.datePlaced, DateFormatType.DATE),
    quoteStatus: row.data.orderStatus,
    orderAttainment: row.data.orderAttainment,
    quoteType: row.data.orderType,
    sublines: sublines,
    account: row.data.account
      ? {
          code: row.data.account.code,
          salesOrg: row.data.account.salesOrg,
          endCustomerCodes: [row.data.account.endCustomerCode],
        }
      : undefined,
  };
};

const transformQuoteLine = (data: QuoteLineMO): QuoteReportDownloadSubLineDTO[] => {
  return data.parts.map((item) => ({
    lineItem: data.lineNo,
    sublineItem: item.lineNo,
    product: data.product.designation,
    description: data.product.description ? data.product.description : data.product.shortDescription.value,
    materialNumber: data.product.sapMaterialNumber,
    customerMaterialNumber:
      data.product.aliases && data.product.aliases.length > 0 ? data.product.aliases.join(", ") : "",
    packagingCode: data.packCode,
    quantity: item.requestedQuantity,
    confirmedQuantity: item.confirmedQuantity,
    shippedQuantity: 0, // UNUSED
    shipment: translateWarehouseName(instance.t, data.warehouseName), // TODO ?
    estimatedDeliveryDate: getEDD(item),
    requestedDeliveryDate: formatNullableDate(item.requestedDeliveryDate, DateFormatType.DATE),
    weight: data.weight ? data.weight.value : undefined,
    weightUnitOfMeasurement: data.weight ? WeightUtils.getPreferredUnitSymbol(data.weight) : "",
    weightUnit: instance.t("common.product.perPiece", { count: 1 }),
    netPrice: item.price ? item.price.value : undefined,
    netPriceCurrency: item.price ? item.price.currencyIso : "",
    priceUnit: instance.t("common.product.perPiece", { count: item.price?.unit ?? 1 }),
    totalNetPrice: getOrderLineItemTotalPrice(item).value,
    currency: item.price ? item.price.currencyIso : "",
    lineItemStatus: data.status,
    comment: data.comment ?? "",
    priceReference: data.priceReference,
    marketDesignation: data.product.marketDesignation,
  }));
};

export const getReportLabels = (): string[] => {
  return [
    "quoteListPage.table.header.quoteNumber",
    "quoteListPage.queryType.po",
    "quoteListPage.table.header.date",
    "quoteDetailsPage.summary.quoteStatus",
    "quoteListPage.table.header.orderAttainment",
    "quoteDetailsPage.summary.quoteType",
    "quoteListPage.table.header.lineItem",
    "quoteListPage.table.header.subLineItem",
    "activeQuotePage.table.header.product.title",
    "quoteListPage.table.header.description",
    "quoteListPage.table.header.materialNumber",
    "quoteListPage.table.header.customerMaterialNumber",
    "activeQuotePage.table.header.packaging.title",
    "quoteListPage.table.header.confirmedQuantity",
    "quoteListPage.table.header.requestedQuantity",
    "activeQuotePage.table.header.packaging.subtitle",
    "quoteListPage.table.header.estimatedDeliveryDate",
    "quoteListPage.table.header.reqDeliveryDate",
    "activeQuotePage.table.weight",
    "quoteListPage.table.header.weightUnitMeasurement",
    "quoteListPage.table.header.weightUnit",
    "quoteListPage.table.header.netPrice",
    "quoteListPage.table.header.currency",
    "quoteDetailsPage.table.header.priceUnit",
    "quoteListPage.table.header.total",
    "quoteListPage.table.header.lineItemStatus",
    "quoteDetailsPage.table.comment",
    "quoteListPage.table.header.priceRefEndCustomer",
    "quoteListPage.table.header.timestamp",
    "quoteListPage.table.header.customer.number",
    "quoteListPage.table.header.marketDesignation",
  ];
};

const showQtyConfirmed = (entry: OrderLineItemData) => {
  return "" + (entry.confirmedQty || "-");
};
